import React from "react";
import PrintIcon from "@mui/icons-material/Print";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
} from "@mui/material";
import moment from "moment";
import "moment/locale/id";
import { useDispatch, useSelector } from "react-redux";
import { fetchCurrentPIN } from "state/userActions";
import PinModal from "components/Modals/PinModal";
moment.locale("id");
const RowItem = ({
  label,
  value,
  isBold = false,
  isRed = false,
  isGrey = false,
  hasBorderBottom = true,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.5,
      px: 2,
      borderBottom: hasBorderBottom ? "0.5px solid #ccc" : "none",
      gap: 1,
    }}
  >
    <Typography
      variant={isBold ? "subtitle1" : "body1"}
      sx={{
        fontWeight: isBold ? "bold" : "normal",
        color: isRed
          ? "error.main"
          : isGrey
            ? "text.secondary"
            : "text.primary",
        flex: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant={isBold ? "subtitle1" : "body1"}
      sx={{
        fontWeight: isBold ? "bold" : "normal",
        color: isRed
          ? "error.main"
          : isGrey
            ? "text.secondary"
            : "text.primary",
        textAlign: "right",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const formatRupiah = (amount) => {
  if (amount === null || amount === undefined) return "Rp 0";
  const isNegative = amount < 0;
  const formatted = Math.abs(amount).toLocaleString("id-ID", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${isNegative ? "(-) " : ""}Rp ${formatted}`;
};

const DetailSetoranHarianModal = ({
  open,
  onClose,
  dataDetail,
  dataPengeluaran,
  listCabang,
  onSaveWarkat,
}) => {
  const hasData = !!dataPengeluaran;
  const dispatch = useDispatch();
  const { currentPin } = useSelector((state) => state.user);
  const [hasVerifyPIN, setHasVerifyPIN] = React.useState(false);
  const [openPinDialog, setOpenPinDialog] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setHasVerifyPIN(false);
    }
  }, [open]);

  const { totalSelisihPlusSemua, totalSelisihMinusSemua } =
    React.useMemo(() => {
      let totalPlus = 0;
      let totalMinus = 0;
      const activeListCabang = listCabang || [];
      activeListCabang.forEach((c) => {
        let originalData = c.original_data;
        if (typeof originalData === "string") {
          try {
            originalData = JSON.parse(originalData);
          } catch (e) {
            originalData = null;
          }
        }
        if (originalData && Array.isArray(originalData)) {
          originalData.forEach((item) => {
            const calc =
              (item.rp_omzet_kasir || 0) +
              (item.rp_debit_credit || 0) +
              (item.rp_ewallet || 0) +
              (item.rp_pengeluaran || 0);
            const selisih = calc - (item.rp_omzet_data || 0);
            if (selisih < 0) {
              totalMinus += selisih;
            } else if (selisih > 0) {
              totalPlus += selisih;
            }
          });
        }
      });
      return {
        totalSelisihPlusSemua: totalPlus,
        totalSelisihMinusSemua: totalMinus,
      };
    }, [listCabang]);

  const sortedListCabang = React.useMemo(() => {
    const list = listCabang ? [...listCabang] : [];
    return list.sort(
      (a, b) => (b.nominal_setoran || 0) - (a.nominal_setoran || 0),
    );
  }, [listCabang]);

  if (!open) return null;

  const hitungPecahan = (hasData, total, masuk, keluar, nominal) => {
    const totalVal = (total ?? 0) / nominal;
    const masukVal = (masuk ?? 0) / nominal;
    const keluarVal = (keluar ?? 0) / nominal;
    const hasil = totalVal + masukVal - keluarVal;
    return !hasData
      ? totalVal
      : `${hasil} (${totalVal} + ${masukVal} - ${keluarVal}) = ${formatRupiah(
          hasil * nominal,
        )}`;
  };
  const isSameDate = moment(dataDetail.tanggal_kas).isSame(
    moment(dataDetail.tanggal_setoran),
    "day",
  );

  const payloadDataWarkat = {
    nama: "Setoran Tunai Harian Cabang",
    tanggal_kas: moment(dataDetail.tanggal_kas).format(),
    tanggal_setoran: isSameDate
      ? moment(dataDetail.tanggal_kas).add(1, "days").format()
      : moment(dataDetail.tanggal_setoran).format(),
    tipe: "pemasukan",
    status: "realisasi",
    catatan: "Setoran dari semua cabang",
    nominal: hasData
      ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Number(dataPengeluaran?.nominal_pendapatan || 0) +
        // -
        // Number(dataPengeluaran?.nominal_pengeluaran || 0) +
        Number(dataDetail?.total_stl_akumulasi || 0) +
        Number(dataDetail?.total_debit_credit_akumulasi || 0) +
        Number(dataDetail?.total_ewallet_akumulasi || 0) +
        // Number(dataDetail?.total_pengeluaran_akumulasi || 0) +
        Number(dataPengeluaran?.transfer_pendapatan || 0)
      : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Number(dataDetail?.total_stl_akumulasi || 0) +
        Math.abs(
          Number(dataDetail?.total_selisih_murni_akumulasi_kondisi || 0),
        ) +
        Number(dataDetail?.total_debit_credit_akumulasi || 0) +
        Number(dataDetail?.total_ewallet_akumulasi || 0),
    // +  Number(dataDetail?.total_pengeluaran_akumulasi || 0),
  };

  const handleSaveWarkat = () => {
    const isConfirmed = window.confirm(
      `Apakah Anda yakin ingin ${
        dataDetail.has_save_warkat ? "memperbaharui" : "menyimpan"
      } data warkat dengan nominal ${formatRupiah(payloadDataWarkat.nominal)}?`,
    );

    if (isConfirmed) {
      onSaveWarkat(payloadDataWarkat);
    }
  };

  const handlePrint = () => {
    const tanggalKas = dataDetail?.tanggal_kas
      ? moment(dataDetail.tanggal_kas).format("DD MMMM YYYY")
      : "-";
    const tanggalSetoran = dataDetail?.tanggal_setoran
      ? moment(dataDetail.tanggal_setoran).format("DD MMMM YYYY")
      : "-";

    const rupiahFmt = (amount) => {
      if (amount === null || amount === undefined) return "Rp 0";
      const isNeg = amount < 0;
      const fmt = Math.abs(amount).toLocaleString("id-ID", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${isNeg ? "(-) " : ""}Rp ${fmt}`;
    };

    const calcPecahan = (total, masuk, keluar, nominal) => {
      const totalVal = (total ?? 0) / nominal;
      const masukVal = (masuk ?? 0) / nominal;
      const keluarVal = (keluar ?? 0) / nominal;
      const hasil = totalVal + masukVal - keluarVal;
      return !hasData
        ? totalVal
        : `${hasil} (${totalVal} + ${masukVal} - ${keluarVal}) = ${rupiahFmt(hasil * nominal)}`;
    };

    const isLocked = (dataDetail?.jumlah_cabang ?? 0) < 12 && !hasVerifyPIN;

    const totalOmzet = hasData
      ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Number(dataPengeluaran?.nominal_pendapatan || 0) +
        Number(dataDetail?.total_stl_akumulasi || 0) +
        Number(dataDetail?.total_debit_credit_akumulasi || 0) +
        Number(dataDetail?.total_ewallet_akumulasi || 0) +
        Number(dataPengeluaran?.transfer_pendapatan || 0)
      : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Number(dataDetail?.total_stl_akumulasi || 0) +
        Math.abs(
          Number(dataDetail?.total_selisih_murni_akumulasi_kondisi || 0),
        ) +
        Number(dataDetail?.total_debit_credit_akumulasi || 0) +
        Number(dataDetail?.total_ewallet_akumulasi || 0);

    const selisihOmzet =
      (totalOmzet + Number(dataDetail?.total_pengeluaran_akumulasi || 0)) -
      Number(dataDetail?.total_omzet_data_akumulasi || 0);

    const pecahanList = [
      {
        label: "100rb",
        nominal: 100000,
        total: dataDetail?.total_lembar_seratus,
        masuk: dataPengeluaran?.seratus_pendapatan,
        keluar: dataPengeluaran?.seratus_pengeluaran,
      },
      {
        label: "50rb",
        nominal: 50000,
        total: dataDetail?.total_lembar_limapuluh,
        masuk: dataPengeluaran?.limapuluh_pendapatan,
        keluar: dataPengeluaran?.limapuluh_pengeluaran,
      },
      {
        label: "20rb",
        nominal: 20000,
        total: dataDetail?.total_lembar_duapuluh,
        masuk: dataPengeluaran?.duapuluh_pendapatan,
        keluar: dataPengeluaran?.duapuluh_pengeluaran,
      },
      {
        label: "10rb",
        nominal: 10000,
        total: dataDetail?.total_lembar_sepuluh,
        masuk: dataPengeluaran?.sepuluh_pendapatan,
        keluar: dataPengeluaran?.sepuluh_pengeluaran,
      },
      {
        label: "5rb",
        nominal: 5000,
        total: dataDetail?.total_lembar_limaribu,
        masuk: dataPengeluaran?.limaribu_pendapatan,
        keluar: dataPengeluaran?.limaribu_pengeluaran,
      },
      {
        label: "2rb",
        nominal: 2000,
        total: dataDetail?.total_lembar_duaribu,
        masuk: dataPengeluaran?.duaribu_pendapatan,
        keluar: dataPengeluaran?.duaribu_pengeluaran,
      },
      {
        label: "1rb",
        nominal: 1000,
        total: dataDetail?.total_lembar_seribu,
        masuk: dataPengeluaran?.seribu_pendapatan,
        keluar: dataPengeluaran?.seribu_pengeluaran,
      },
    ];

    const pecahanRows = pecahanList
      .map(
        (p) =>
          `<tr><td>Pecahan ${p.label}</td><td>${calcPecahan(p.total, p.masuk, p.keluar, p.nominal)}</td></tr>`,
      )
      .join("");

    const coinVal = !hasData
      ? rupiahFmt(dataDetail?.total_coin ?? 0)
      : `(${rupiahFmt(dataDetail?.total_coin ?? 0)} + ${rupiahFmt(dataPengeluaran?.coin_pendapatan ?? 0)} - ${rupiahFmt(dataPengeluaran?.coin_pengeluaran ?? 0)}) = ${rupiahFmt((Number(dataDetail?.total_coin) ?? 0) + (Number(dataPengeluaran?.coin_pendapatan) ?? 0) - (Number(dataPengeluaran?.coin_pengeluaran) ?? 0))}`;

    const catatanRows = dataDetail?.catatan_akumulasi
      ? dataDetail.catatan_akumulasi
          .split(" ; ")
          .filter((c) => c.trim())
          .map(
            (c, i) => `<p class="note"><b>Note ${i + 1}:</b> ${c.trim()}</p>`,
          )
          .join("")
      : "";

    const pendapatanListHtml =
      hasData && dataPengeluaran?.list_pendapatan
        ? dataPengeluaran.list_pendapatan
            .split(";")
            .map((item, i) => `<p>${i + 1}. ${item.trim()}</p>`)
            .join("")
        : "<p>-</p>";

    const pengeluaranListHtml =
      hasData && dataPengeluaran?.list_pengeluaran
        ? dataPengeluaran.list_pengeluaran
            .split(";")
            .map((item, i) => `<p>${i + 1}. ${item.trim()}</p>`)
            .join("")
        : "<p>-</p>";

    const activeListCabang = (listCabang || [])
      .slice()
      .sort((a, b) => (b.nominal_setoran || 0) - (a.nominal_setoran || 0));

    const printCabangRows = activeListCabang
      .map((c, i) => {
        const tunaiVal =
          c.nominal_setoran === c.transfer ? 0 : c.nominal_setoran || 0;
        const rowTotal =
          tunaiVal +
          (c.transfer || 0) +
          (c.total_debit_credit || 0) +
          (c.total_ewallet || 0) +
          (c.total_stl || 0);

        let minus = 0;
        let plus = 0;
        let originalData = c.original_data;
        if (typeof originalData === "string") {
          try {
            originalData = JSON.parse(originalData);
          } catch (e) {
            originalData = null;
          }
        }
        if (originalData && Array.isArray(originalData)) {
          originalData.forEach((item) => {
            const calc =
              (item.rp_omzet_kasir || 0) +
              (item.rp_debit_credit || 0) +
              (item.rp_ewallet || 0) +
              (item.rp_pengeluaran || 0);
            const selisih = calc - (item.rp_omzet_data || 0);
            if (selisih < 0) {
              minus += selisih;
            } else if (selisih > 0) {
              plus += selisih;
            }
          });
        }

        return `<tr>
            <td style="text-align:center">${i + 1}</td>
            <td style="text-align:left">${c.nama_cab} - ${c.nama_karyawan}</td>
            <td style="text-align:right">${c.total_struk}</td>
            <td style="text-align:right">${rupiahFmt(tunaiVal)}</td>
            <td style="text-align:right">${rupiahFmt(c.transfer)}</td>
            <td style="text-align:right">${rupiahFmt(c.total_debit_credit)}</td>
            <td style="text-align:right">${rupiahFmt(c.total_ewallet)}</td>
            <td style="text-align:right">${rupiahFmt(c.total_pengeluaran)}</td>
            <td style="text-align:right">${rupiahFmt(c.total_stl)}</td>
            <td class="bold" style="text-align:right">${rupiahFmt(rowTotal)}</td>
            <td class="bold" style="text-align:right">${rupiahFmt(c.total_omzet_data)}</td>
            <td style="text-align:right">${rupiahFmt(plus)}</td>
            <td style="text-align:right">${rupiahFmt(minus)}</td>
          </tr>`;
      })
      .join("");

    const totalCabangTunai = activeListCabang.reduce(
      (sum, item) =>
        sum +
        (item.nominal_setoran === item.transfer
          ? 0
          : item.nominal_setoran || 0),
      0,
    );
    const totalCabangTransfer = activeListCabang.reduce(
      (sum, item) => sum + (item.transfer || 0),
      0,
    );
    const totalCabangEDC = activeListCabang.reduce(
      (sum, item) => sum + (item.total_debit_credit || 0),
      0,
    );
    const totalCabangQRIS = activeListCabang.reduce(
      (sum, item) => sum + (item.total_ewallet || 0),
      0,
    );
    const totalCabangPengeluaran = activeListCabang.reduce(
      (sum, item) => sum + (item.total_pengeluaran || 0),
      0,
    );
    const totalCabangSTL = activeListCabang.reduce(
      (sum, item) => sum + (item.total_stl || 0),
      0,
    );
    const totalCabangSemua =
      totalCabangTunai +
      totalCabangTransfer +
      totalCabangEDC +
      totalCabangQRIS +
      totalCabangSTL;
    const totalCabangOmzetData = activeListCabang.reduce(
      (sum, item) => sum + (item.total_omzet_data || 0),
      0,
    );

    const printCabangSectionHtml = `
      <div class="rincian-section">
        <h3 style="text-align:center;margin-bottom:12px;font-size:14px;font-weight:bold;letter-spacing:0.5px">Rincian Setoran Toko Cabang</h3>
        <table class="cabang-table">
          <thead>
            <tr style="background:#f5f5f5;font-weight:bold;text-align:right">
              <td style="text-align:center;width:40px">No</td>
              <td style="text-align:left">Nama Cabang & Penyetor</td>
              <td style="text-align:right">STD</td>
              <td style="text-align:right">Tunai</td>
              <td style="text-align:right">Transfer</td>
              <td style="text-align:right">EDC Machine</td>
              <td style="text-align:right">QRIS</td>
              <td style="text-align:right">Pengeluaran</td>
              <td style="text-align:right">STL</td>
              <td style="text-align:right">Total Setoran</td>
              <td style="text-align:right">Total Omzet Data</td>
              <td style="text-align:right">Selisih (+)</td>
              <td style="text-align:right">Selisih (-)</td>
            </tr>
          </thead>
          <tbody>
            ${printCabangRows}
            <tr class="bold" style="background:#fafafa;text-align:right">
              <td colspan="2" style="text-align:center">TOTAL KESELURUHAN</td>
              <td style="text-align:right">${activeListCabang.reduce((acc, c) => acc + parseInt(c.total_struk || 0), 0)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangTunai)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangTransfer)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangEDC)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangQRIS)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangPengeluaran)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangSTL)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangSemua)}</td>
              <td style="text-align:right">${rupiahFmt(totalCabangOmzetData)}</td>
              <td style="text-align:right">${rupiahFmt(totalSelisihPlusSemua)}</td>
              <td style="text-align:right">${rupiahFmt(totalSelisihMinusSemua)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    const totalUangCash = hasData
      ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) -
        Number(dataDetail?.total_transfer || 0) +
        Number(dataPengeluaran?.nominal_pendapatan || 0)
      : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Math.abs(
          Number(dataDetail?.total_selisih_murni_akumulasi_kondisi || 0),
        );

    const subtotalOmzet = hasData
      ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Number(dataPengeluaran?.nominal_pendapatan || 0) +
        Number(dataPengeluaran?.transfer_pendapatan || 0)
      : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
        Math.abs(
          Number(dataDetail?.total_selisih_murni_akumulasi_kondisi || 0),
        );

    const totalUangTransfer = hasData
      ? Number(dataPengeluaran?.transfer_pendapatan || 0) +
        Number(dataDetail?.total_transfer || 0)
      : Number(dataDetail?.total_transfer || 0);

    const selisihRow = hasData
      ? `<tr><td>Pendapatan (+)</td><td class="green">${rupiahFmt(dataPengeluaran?.nominal_pendapatan || 0)}</td></tr>`
      : `<tr><td>Selisih Minus</td><td>${rupiahFmt(dataDetail?.total_selisih_murni_akumulasi_kondisi)}</td></tr>`;

    const transferPendapatanRow = hasData
      ? `<tr><td>Pendapatan Transfer (+)</td><td class="green">${rupiahFmt(dataPengeluaran?.transfer_pendapatan || 0)}</td></tr>`
      : "";

    const serverOmzetRow = isLocked
      ? `<tr class="bold" style="filter: blur(3px); user-select: none;"><td>Total Omzet Data Server</td><td>${rupiahFmt(Number(dataDetail?.total_omzet_data_akumulasi || 0))}</td></tr>`
      : `<tr class="bold"><td>Total Omzet Data Server</td><td>${rupiahFmt(Number(dataDetail?.total_omzet_data_akumulasi || 0))}</td></tr>`;

    const selisihOmzetRow = isLocked
      ? `<tr class="bold" style="filter: blur(3px); user-select: none;"><td>Total Selisih Omzet</td><td>${rupiahFmt(selisihOmzet)}</td></tr>`
      : `<tr class="bold"><td>Total Selisih Omzet</td><td class="${selisihOmzet < 0 ? "red" : ""}">${rupiahFmt(selisihOmzet)}</td></tr>`;

    const htmlContent = `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Detail Setoran Global - ${tanggalSetoran}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #222; background: #fff; padding: 15px; }
    h1 { font-size: 16px; text-align: center; font-weight: bold; margin-bottom: 2px; }
    .subtitle { text-align: center; color: #555; font-size: 11px; margin-bottom: 2px; }
    .divider { border: none; border-top: 1px solid #bbb; margin: 8px 0; }
    .three-col { display: flex; gap: 12px; margin-top: 12px; }
    .col-rincian { flex: 1.2; }
    .col-pecahan { flex: 1.2; }
    .col-pendapatan-pengeluaran { flex: 1; border: 1px solid #eee; border-radius: 4px; padding: 8px; }
    .col-pendapatan, .col-pengeluaran { padding: 4px; }
    table { width: 100%; border-collapse: collapse; }
    tr { border-bottom: 0.5px solid #ddd; }
    td { padding: 4px 6px; vertical-align: middle; }
    td:last-child { text-align: right; }
    .bold td { font-weight: bold; }
    .col-title { font-weight: bold; font-size: 12px; padding: 5px 6px; background: #f5f5f5; text-align: center !important; }
    .note { font-size: 10px; color: #666; padding: 4px 6px; }
    .green { color: #2e7d32; }
    .red { color: #c62828; }
    .rincian-section { margin-top: 12px; border-top: 1px solid #bbb; padding-top: 8px; }
    .label-sm { font-size: 10px; color: #777; margin-top: 4px; }
    .bold { font-weight: bold; }
    .cabang-table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 10px; }
    .cabang-table td { padding: 4px 6px; border: 0.5px solid #ddd; vertical-align: middle; }
    .cabang-table thead td { font-weight: bold; }
    .cabang-table tbody tr:nth-child(even) { background-color: #fafafa; }
    .col-pendapatan h4, .col-pengeluaran h4 { font-size: 11px; font-weight: bold; margin-bottom: 4px; border-bottom: 0.5px solid #ccc; padding-bottom: 4px; text-align: center; }
    .col-pendapatan p, .col-pengeluaran p { font-size: 10px; margin-top: 3px; border-bottom: 0.5px solid #eee; padding-bottom: 3px; text-align: left; color: #222; }
    .col-pendapatan p.label-sm, .col-pengeluaran p.label-sm { border-bottom: none; padding-bottom: 0; margin-top: 4px; font-size: 9px; color: #777; }
    .col-pendapatan p.bold, .col-pengeluaran p.bold { font-weight: bold; border-bottom: 0.5px solid #ccc; padding-bottom: 3px; font-size: 11px; }
    @media print {
      body { padding: 5px; }
      @page { size: landscape; margin: 10mm; }
    }
  </style>
</head>
<body>
  <h1>DETAIL SETORAN GLOBAL</h1>
  <p class="subtitle">Prima Centre DC - Sukabumi</p>
  <p class="subtitle">Sinkron - ${tanggalKas} || Setoran - ${tanggalSetoran}</p>
  <hr class="divider" />

  <div class="three-col">
    <!-- Kolom 1: Ringkasan Setoran -->
    <div class="col-rincian">
      <table>
        <tr><td class="col-title" colspan="2">Ringkasan Setoran</td></tr>
        <tr><td>Jumlah Cabang</td><td>${dataDetail?.jumlah_cabang?.toString() || "0"}</td></tr>
        <tr class="bold"><td>Uang Cash</td><td>${rupiahFmt(Number(dataDetail?.total_setoran_cash_akumulasi || 0) - Number(dataDetail?.total_transfer || 0))}</td></tr>
        ${selisihRow}
        <tr class="bold"><td>Total Uang Cash</td><td>${rupiahFmt(totalUangCash)}</td></tr>
        ${transferPendapatanRow}
        <tr class="bold"><td>Uang Transfer</td><td>${rupiahFmt(dataDetail?.total_transfer)}</td></tr>
        <tr class="bold"><td>Total Uang Transfer</td><td>${rupiahFmt(totalUangTransfer)}</td></tr>
        <tr class="bold"><td>Subtotal Omzet</td><td>${rupiahFmt(subtotalOmzet)}</td></tr>
        <tr><td>STL</td><td>${rupiahFmt(dataDetail?.total_stl_akumulasi)}</td></tr>
        <tr><td>EDC Machine</td><td>${rupiahFmt(dataDetail?.total_debit_credit_akumulasi)}</td></tr>
        <tr><td>Qris</td><td>${rupiahFmt(dataDetail?.total_ewallet_akumulasi)}</td></tr>
        <tr class="bold"><td>Total Omzet</td><td>${rupiahFmt(totalOmzet)}</td></tr>
        <tr><td>Pengeluaran</td><td>${rupiahFmt(dataDetail?.total_pengeluaran_akumulasi)}</td></tr>
        ${serverOmzetRow}
        ${selisihOmzetRow}
      </table>
      ${catatanRows}
    </div>

    <!-- Kolom 2: Rincian Pecahan -->
    <div class="col-pecahan">
      <table>
        <tr><td class="col-title" colspan="2">Rincian Pecahan</td></tr>
        ${pecahanRows}
        <tr><td>Pecahan Coin</td><td>${coinVal}</td></tr>
        <tr><td>Nominal Uang Cash</td><td>${rupiahFmt((dataDetail?.total_setoran_cash_akumulasi ?? 0) - (dataDetail?.total_transfer ?? 0))}</td></tr>
        <tr><td>Nominal Pendapatan</td><td>${rupiahFmt(dataPengeluaran?.nominal_pendapatan ?? 0)}</td></tr>
        <tr class="bold"><td>Total Uang Cash</td><td>${rupiahFmt(parseInt(dataDetail?.total_setoran_cash_akumulasi || 0) - parseInt(dataDetail?.total_transfer || 0) + parseInt(dataPengeluaran?.nominal_pendapatan || 0))}</td></tr>
        <tr><td>Nominal Pengeluaran</td><td>-${rupiahFmt(dataPengeluaran?.nominal_pengeluaran || 0)}</td></tr>
        <tr class="bold"><td>Total Akhir Uang Cash</td><td>${rupiahFmt(parseInt(dataDetail?.total_setoran_cash_akumulasi ?? 0) - parseInt(dataDetail?.total_transfer ?? 0) + parseInt(dataPengeluaran?.nominal_pendapatan ?? 0) - parseInt(dataPengeluaran?.nominal_pengeluaran ?? 0))}</td></tr>
      </table>
    </div>

    <!-- Kolom 3: Pendapatan & Pengeluaran -->
    <div class="col-pendapatan-pengeluaran">
      <!-- Pendapatan -->
      <div class="col-pendapatan">
        <h4 style="color:#2e7d32">PENDAPATAN</h4>
        ${
          hasData && dataPengeluaran
            ? `
              <p class="label-sm">Total Pendapatan</p>
              <p class="bold green">${rupiahFmt(dataPengeluaran?.nominal_pendapatan)}</p>
              <p class="label-sm" style="font-weight: bold;">List Pendapatan:</p>
              ${pendapatanListHtml}
            `
            : `<p style="color: #777; text-align: center; margin-top: 20px; border-bottom: none;">Tidak ada data pendapatan</p>`
        }
      </div>

      <hr class="divider" style="margin: 12px 0;" />

      <!-- Pengeluaran -->
      <div class="col-pengeluaran">
        <h4 style="color:#c62828">PENGELUARAN</h4>
        ${
          hasData && dataPengeluaran
            ? `
              <p class="label-sm">Total Pengeluaran</p>
              <p class="bold red">${rupiahFmt(dataPengeluaran?.nominal_pengeluaran)}</p>
              <p class="label-sm" style="font-weight: bold;">List Pengeluaran:</p>
              ${pengeluaranListHtml}
            `
            : `<p style="color: #777; text-align: center; margin-top: 20px; border-bottom: none;">Tidak ada data pengeluaran</p>`
        }
      </div>
    </div>
  </div>

  ${printCabangSectionHtml}

  <script>
    window.onload = function() { window.print(); };
  </script>
</body>
</html>`;

    const tab = window.open("", "_blank");
    if (tab) {
      tab.document.open();
      tab.document.write(htmlContent);
      tab.document.close();
    }
  };

  console.log(dataDetail);
  console.log(listCabang);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { width: "95 vw", maxHeight: "95 vh" },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          DETAIL SETORAN GLOBAL
        </Typography>
        <Typography color="text.secondary">
          Prima Centre DC - Sukabumi
        </Typography>
        <Typography color="text.secondary">
          {`Sinkron - ${
            dataDetail?.tanggal_kas
              ? moment(dataDetail.tanggal_kas).format("DD MMMM YYYY")
              : "-"
          } || Setoran - ${
            dataDetail?.tanggal_setoran
              ? moment(dataDetail.tanggal_setoran).format("DD MMMM YYYY")
              : "-"
          }`}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: "auto", maxHeight: "95 vh" }}>
        {/* BAGIAN KIRI */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            borderBottom: "0.5px solid #ccc",
          }}
        >
          {/* KOLOM 1: BAGIAN KIRI (Rincian Akumulasi) */}
          <Box
            sx={{
              flex: 1,
              borderRight: { xs: "none", md: "1px solid #eee" },
              borderBottom: { xs: "1px solid #eee", md: "none" },
              px: 2,
              py: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 1.5,
                color: "secondary.main",
                textAlign: "center",
              }}
            >
              RINCIAN AKUMULASI
            </Typography>
            <RowItem
              label="Jumlah Cabang"
              value={dataDetail?.jumlah_cabang?.toString() || "0"}
            />
            <RowItem
              label="Uang Cash"
              value={formatRupiah(
                dataDetail?.total_setoran_cash_akumulasi -
                  dataDetail?.total_transfer,
              )}
            />

            {hasData && (
              <>
                <Box
                  sx={{
                    paddingY: 0.5,
                    paddingX: 2,
                    borderBottom: "0.5px solid #ccc",
                    backgroundColor: "#e8f5e9",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "500",
                        color: "success.main",
                      }}
                    >
                      Pendapatan (+)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "success.main", fontWeight: "500" }}
                    >
                      {formatRupiah(dataPengeluaran?.nominal_pendapatan || 0)}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {!hasData && (
              <RowItem
                label="Selisih Minus"
                value={formatRupiah(
                  dataDetail?.total_selisih_murni_akumulasi_kondisi,
                )}
              />
            )}
            <RowItem
              label="Total Uang Cash"
              isBold
              value={formatRupiah(
                hasData
                  ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) -
                      Number(dataDetail?.total_transfer || 0) +
                      Number(dataPengeluaran?.nominal_pendapatan || 0)
                  : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
                      Math.abs(
                        Number(
                          dataDetail?.total_selisih_murni_akumulasi_kondisi ||
                            0,
                        ),
                      ),
              )}
            />
            {hasData && (
              <Box
                sx={{
                  paddingY: 0.5,
                  paddingX: 2,
                  borderBottom: "0.5px solid #ccc",
                  backgroundColor: "#e8f5e9",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "500",
                      color: "success.main",
                    }}
                  >
                    Pendapatan Transfer (+)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "success.main", fontWeight: "500" }}
                  >
                    {formatRupiah(dataPengeluaran?.transfer_pendapatan || 0)}
                  </Typography>
                </Box>
              </Box>
            )}

            <RowItem
              label="Uang Transfer"
              value={formatRupiah(dataDetail?.total_transfer)}
            />
            <RowItem
              label="Total Uang Transfer"
              isBold
              value={formatRupiah(
                hasData
                  ? Number(dataPengeluaran?.transfer_pendapatan || 0) +
                      Number(dataDetail?.total_transfer || 0)
                  : Number(dataDetail?.total_transfer || 0),
              )}
            />
            <RowItem
              label="Subtotal Omzet"
              isBold
              value={formatRupiah(
                hasData
                  ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
                      Number(dataPengeluaran?.nominal_pendapatan || 0) +
                      Number(dataPengeluaran?.transfer_pendapatan || 0)
                  : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
                      Math.abs(
                        Number(
                          dataDetail?.total_selisih_murni_akumulasi_kondisi ||
                            0,
                        ),
                      ),
              )}
            />

            <RowItem
              label="STL"
              value={formatRupiah(dataDetail?.total_stl_akumulasi)}
            />
            <RowItem
              label="EDC Machine"
              value={formatRupiah(dataDetail?.total_debit_credit_akumulasi)}
            />
            <RowItem
              label="Qris"
              value={formatRupiah(dataDetail?.total_ewallet_akumulasi)}
            />
            <RowItem
              label="Total Omzet"
              isBold
              value={formatRupiah(
                hasData
                  ? Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
                      Number(dataPengeluaran?.nominal_pendapatan || 0) +
                      Number(dataDetail?.total_stl_akumulasi || 0) +
                      Number(dataDetail?.total_debit_credit_akumulasi || 0) +
                      Number(dataDetail?.total_ewallet_akumulasi || 0) +
                      Number(dataPengeluaran?.transfer_pendapatan || 0)
                  : Number(dataDetail?.total_setoran_cash_akumulasi || 0) +
                      Number(dataDetail?.total_stl_akumulasi || 0) +
                      Math.abs(
                        Number(
                          dataDetail?.total_selisih_murni_akumulasi_kondisi ||
                            0,
                        ),
                      ) +
                      Number(dataDetail?.total_debit_credit_akumulasi || 0) +
                      Number(dataDetail?.total_ewallet_akumulasi || 0),
              )}
            />
            <RowItem
              label="Pengeluaran"
              value={formatRupiah(dataDetail?.total_pengeluaran_akumulasi)}
            />
            {(() => {
              const isLocked =
                (dataDetail?.jumlah_cabang ?? 0) < 12 && !hasVerifyPIN;

              return (
                <Box
                  onClick={() => {
                    if (isLocked) {
                      dispatch(fetchCurrentPIN());
                      setOpenPinDialog(true);
                    }
                  }}
                  sx={{
                    cursor: isLocked ? "pointer" : "default",
                    position: "relative",
                  }}
                >
                  {isLocked && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          color: "white",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: "bold",
                          fontSize: "11px",
                          boxShadow: 2,
                        }}
                      >
                        Klik untuk membuka (PIN)
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      filter: isLocked ? "blur(3.5px)" : "none",
                      transition: "filter 0.3s",
                      pointerEvents: isLocked ? "none" : "auto",
                    }}
                  >
                    <RowItem
                      label="Total Omzet Data Server"
                      isBold
                      value={formatRupiah(
                        Number(dataDetail?.total_omzet_data_akumulasi || 0),
                      )}
                    />
                    <RowItem
                      label="Total Selisih Omzet"
                      isBold
                      value={formatRupiah(
                        (hasData
                          ? Number(
                              dataDetail?.total_setoran_cash_akumulasi || 0,
                            ) +
                            Number(dataPengeluaran?.nominal_pendapatan || 0) +
                            Number(dataDetail?.total_stl_akumulasi || 0) +
                            Number(
                              dataDetail?.total_debit_credit_akumulasi || 0,
                            ) +
                            Number(dataDetail?.total_ewallet_akumulasi || 0) +
                            Number(
                              dataDetail?.total_pengeluaran_akumulasi || 0,
                            ) +
                            Number(dataPengeluaran?.transfer_pendapatan || 0)
                          : Number(
                              dataDetail?.total_setoran_cash_akumulasi || 0,
                            ) +
                            Number(dataDetail?.total_stl_akumulasi || 0) +
                            Math.abs(
                              Number(
                                dataDetail?.total_selisih_murni_akumulasi_kondisi ||
                                  0,
                              ),
                            ) +
                            Number(
                              dataDetail?.total_debit_credit_akumulasi || 0,
                            ) +
                            Number(dataDetail?.total_ewallet_akumulasi || 0) +
                            Number(
                              dataDetail?.total_pengeluaran_akumulasi || 0,
                            )) -
                          Number(dataDetail?.total_omzet_data_akumulasi || 0),
                      )}
                      isRed={
                        (hasData
                          ? Number(
                              dataDetail?.total_setoran_cash_akumulasi || 0,
                            ) +
                            Number(dataPengeluaran?.nominal_pendapatan || 0) +
                            Number(dataDetail?.total_stl_akumulasi || 0) +
                            Number(
                              dataDetail?.total_debit_credit_akumulasi || 0,
                            ) +
                            Number(dataDetail?.total_ewallet_akumulasi || 0) +
                            Number(
                              dataDetail?.total_pengeluaran_akumulasi || 0,
                            ) +
                            Number(dataPengeluaran?.transfer_pendapatan || 0)
                          : Number(
                              dataDetail?.total_setoran_cash_akumulasi || 0,
                            ) +
                            Number(dataDetail?.total_stl_akumulasi || 0) +
                            Math.abs(
                              Number(
                                dataDetail?.total_selisih_murni_akumulasi_kondisi ||
                                  0,
                              ),
                            ) +
                            Number(
                              dataDetail?.total_debit_credit_akumulasi || 0,
                            ) +
                            Number(dataDetail?.total_ewallet_akumulasi || 0) +
                            Number(
                              dataDetail?.total_pengeluaran_akumulasi || 0,
                            )) -
                          Number(dataDetail?.total_omzet_data_akumulasi || 0) <
                        0
                      }
                    />
                  </Box>
                </Box>
              );
            })()}
            <Divider />
          </Box>

          {/* KOLOM 2: BAGIAN KANAN (Rincian Pecahan Cash) */}
          <Box
            sx={{
              flex: 1,
              borderRight: { xs: "none", md: "1px solid #eee" },
              borderBottom: { xs: "1px solid #eee", md: "none" },
              px: 2,
              py: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 1.5,
                color: "secondary.main",
                textAlign: "center",
              }}
            >
              RINCIAN PECAHAN CASH
            </Typography>
            {[
              {
                label: "100rb",
                nominal: 100000,
                total: dataDetail?.total_lembar_seratus,
                masuk: dataPengeluaran?.seratus_pendapatan,
                keluar: dataPengeluaran?.seratus_pengeluaran,
              },
              {
                label: "50rb",
                nominal: 50000,
                total: dataDetail?.total_lembar_limapuluh,
                masuk: dataPengeluaran?.limapuluh_pendapatan,
                keluar: dataPengeluaran?.limapuluh_pengeluaran,
              },
              {
                label: "20rb",
                nominal: 20000,
                total: dataDetail?.total_lembar_duapuluh,
                masuk: dataPengeluaran?.duapuluh_pendapatan,
                keluar: dataPengeluaran?.duapuluh_pengeluaran,
              },
              {
                label: "10rb",
                nominal: 10000,
                total: dataDetail?.total_lembar_sepuluh,
                masuk: dataPengeluaran?.sepuluh_pendapatan,
                keluar: dataPengeluaran?.sepuluh_pengeluaran,
              },
              {
                label: "5rb",
                nominal: 5000,
                total: dataDetail?.total_lembar_limaribu,
                masuk: dataPengeluaran?.limaribu_pendapatan,
                keluar: dataPengeluaran?.limaribu_pengeluaran,
              },
              {
                label: "2rb",
                nominal: 2000,
                total: dataDetail?.total_lembar_duaribu,
                masuk: dataPengeluaran?.duaribu_pendapatan,
                keluar: dataPengeluaran?.duaribu_pengeluaran,
              },
              {
                label: "1rb",
                nominal: 1000,
                total: dataDetail?.total_lembar_seribu,
                masuk: dataPengeluaran?.seribu_pendapatan,
                keluar: dataPengeluaran?.seribu_pengeluaran,
              },
            ].map((p, i) => (
              <RowItem
                key={i}
                label={`Pecahan ${p.label}`}
                value={hitungPecahan(
                  hasData,
                  p.total,
                  p.masuk,
                  p.keluar,
                  p.nominal,
                )}
              />
            ))}

            <RowItem
              label="Pecahan Coin"
              value={
                !hasData
                  ? formatRupiah(dataDetail?.total_coin ?? 0)
                  : `(${formatRupiah(
                      dataDetail?.total_coin ?? 0,
                    )} + ${formatRupiah(
                      dataPengeluaran?.coin_pendapatan ?? 0,
                    )} - ${formatRupiah(
                      dataPengeluaran?.coin_pengeluaran ?? 0,
                    )}) = ${formatRupiah(
                      (Number(dataDetail?.total_coin) ?? 0) +
                        (Number(dataPengeluaran?.coin_pendapatan) ?? 0) -
                        (Number(dataPengeluaran?.coin_pengeluaran) ?? 0),
                    )}`
              }
            />
            <Divider />

            <RowItem
              label="Nominal Uang Cash"
              value={formatRupiah(
                (dataDetail?.total_setoran_cash_akumulasi ?? 0) -
                  (dataDetail?.total_transfer ?? 0),
              )}
              hasBorderBottom={false}
            />
            <RowItem
              label="Nominal Pendapatan"
              value={formatRupiah(dataPengeluaran?.nominal_pendapatan ?? 0)}
              hasBorderBottom={false}
            />
            <Divider />
            <RowItem
              label="Total Uang Cash"
              value={formatRupiah(
                parseInt(dataDetail?.total_setoran_cash_akumulasi || 0) -
                  parseInt(dataDetail?.total_transfer || 0) +
                  parseInt(dataPengeluaran?.nominal_pendapatan || 0),
              )}
              hasBorderBottom={false}
            />
            <RowItem
              label="Nominal Pengeluaran"
              value={`-${formatRupiah(dataPengeluaran?.nominal_pengeluaran || 0)}`}
              hasBorderBottom={false}
            />
            <Divider />
            <RowItem
              label="Total Akhir Uang Cash"
              value={formatRupiah(
                parseInt(dataDetail?.total_setoran_cash_akumulasi ?? 0) -
                  parseInt(dataDetail?.total_transfer ?? 0) +
                  parseInt(dataPengeluaran?.nominal_pendapatan ?? 0) -
                  parseInt(dataPengeluaran?.nominal_pengeluaran ?? 0),
              )}
              hasBorderBottom={false}
              isBold
            />
            {dataDetail?.catatan_akumulasi &&
              dataDetail.catatan_akumulasi
                .split(" ; ")
                .filter((item) => item.trim())
                .map((item, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{ mt: 1, px: 2, color: "text.secondary" }}
                  >
                    <b>Note {i + 1}:</b> {item.trim()}
                  </Typography>
                ))}
          </Box>

          {/* KOLOM 3: PENDAPATAN & PENGELUARAN */}
          <Box
            sx={{
              flex: 1,
              px: 2,
              py: 1,
            }}
          >
            {/* PENDAPATAN */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  mb: 1.5,
                  color: "success.main",
                  textAlign: "center",
                }}
              >
                PENDAPATAN
              </Typography>
              {hasData && dataPengeluaran ? (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Pendapatan
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="success.main"
                    mb={2}
                    sx={{ borderBottom: "0.5px solid #ccc", pb: 1 }}
                  >
                    {formatRupiah(dataPengeluaran?.nominal_pendapatan ?? 0)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={1}
                    fontWeight="600"
                  >
                    List Pendapatan:
                  </Typography>
                  {dataPengeluaran.list_pendapatan ? (
                    dataPengeluaran.list_pendapatan
                      .split(";")
                      .map((item, i) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{
                            py: 0.5,
                            borderBottom: "0.5px solid #eee",
                            color: "text.primary",
                          }}
                        >
                          {i + 1}. {item.trim()}
                        </Typography>
                      ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mt={4}
                >
                  Tidak ada data pendapatan
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* PENGELUARAN */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  mb: 1.5,
                  color: "error.main",
                  textAlign: "center",
                }}
              >
                PENGELUARAN
              </Typography>
              {hasData && dataPengeluaran ? (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Pengeluaran
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="error.main"
                    mb={2}
                    sx={{ borderBottom: "0.5px solid #ccc", pb: 1 }}
                  >
                    {formatRupiah(dataPengeluaran?.nominal_pengeluaran)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={1}
                    fontWeight="600"
                  >
                    List Pengeluaran:
                  </Typography>
                  {dataPengeluaran.list_pengeluaran ? (
                    dataPengeluaran.list_pengeluaran
                      .split(";")
                      .map((item, i) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{
                            py: 0.5,
                            borderBottom: "0.5px solid #eee",
                            color: "text.primary",
                          }}
                        >
                          {i + 1}. {item.trim()}
                        </Typography>
                      ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mt={4}
                >
                  Tidak ada data pengeluaran
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* BAGIAN BAWAH: RINCIAN SETORAN CABANG */}
        <Box sx={{ p: 3, borderTop: "1px solid #eee" }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "bold",
              mb: 1.5,
              color: "secondary.main",
              textAlign: "center",
            }}
          >
            RINCIAN SETORAN CABANG
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.12)",
              backgroundColor: "transparent",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Nama Cabang & Penyetor
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    STD
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Tunai
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Transfer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    EDC Machine
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    QRIS
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Pengeluaran
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    STL
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Total Setoran
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Total Omzet Data
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Selisih (+)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                    }}
                  >
                    Selisih (-)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedListCabang.map((cabang, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:hover": {
                        backgroundColor: (t) =>
                          t.palette.mode === "dark"
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.01)",
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: "600" }}>
                      {`${cabang.nama_cab} - ${cabang.nama_karyawan}`}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {cabang.total_struk}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {formatRupiah(
                        cabang.nominal_setoran === cabang.transfer
                          ? 0
                          : cabang.nominal_setoran,
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {formatRupiah(cabang.transfer)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {formatRupiah(cabang.total_debit_credit)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {formatRupiah(cabang.total_ewallet)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {formatRupiah(cabang.total_pengeluaran)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "600", color: "text.primary" }}
                    >
                      {formatRupiah(cabang.total_stl)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "700", color: "text.primary" }}
                    >
                      {formatRupiah(
                        (cabang.nominal_setoran === cabang.transfer
                          ? 0
                          : cabang.nominal_setoran) +
                          cabang.transfer +
                          cabang.total_debit_credit +
                          cabang.total_ewallet +
                          cabang.total_stl,
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "700", color: "text.primary" }}
                    >
                      {formatRupiah(cabang.total_omzet_data)}
                    </TableCell>
                    {(() => {
                      let minus = 0;
                      let plus = 0;
                      let originalData = cabang.original_data;
                      if (typeof originalData === "string") {
                        try {
                          originalData = JSON.parse(originalData);
                        } catch (e) {
                          originalData = null;
                        }
                      }
                      if (originalData && Array.isArray(originalData)) {
                        originalData.forEach((item) => {
                          const calc =
                            (item.rp_omzet_kasir || 0) +
                            (item.rp_debit_credit || 0) +
                            (item.rp_ewallet || 0) +
                            (item.rp_pengeluaran || 0);
                          const selisih = calc - (item.rp_omzet_data || 0);
                          if (selisih < 0) {
                            minus += selisih;
                          } else if (selisih > 0) {
                            plus += selisih;
                          }
                        });
                      }
                      return (
                        <>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "600", color: "text.primary" }}
                          >
                            {formatRupiah(plus)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "600", color: "text.primary" }}
                          >
                            {formatRupiah(minus)}
                          </TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter
                sx={{
                  backgroundColor: (t) =>
                    t.palette.mode === "dark" ? "#222" : "#f5f5f5",
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: "800", fontSize: "13px" }}>
                    TOTAL KESELURUHAN
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {listCabang.reduce(
                      (acc, c) => acc + parseInt(c.total_struk),
                      0,
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce(
                        (acc, c) =>
                          acc +
                          (c.nominal_setoran === c.transfer
                            ? 0
                            : c.nominal_setoran),
                        0,
                      ),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce((acc, c) => acc + c.transfer, 0),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce(
                        (acc, c) => acc + c.total_debit_credit,
                        0,
                      ),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce((acc, c) => acc + c.total_ewallet, 0),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce(
                        (acc, c) => acc + c.total_pengeluaran,
                        0,
                      ),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce((acc, c) => acc + c.total_stl, 0),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce(
                        (acc, c) =>
                          acc +
                          (c.nominal_setoran === c.transfer
                            ? 0
                            : c.nominal_setoran) +
                          c.transfer +
                          c.total_debit_credit +
                          c.total_ewallet +
                          c.total_stl,
                        0,
                      ),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(
                      listCabang.reduce(
                        (acc, c) => acc + c.total_omzet_data,
                        0,
                      ),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(totalSelisihPlusSemua)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "800",
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {formatRupiah(totalSelisihMinusSemua)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={handleSaveWarkat}
          variant="contained"
          sx={{
            fontWeight: "bold",
            flexGrow: 1,
            backgroundColor: (t) => t.palette.secondary[300],
            color: "white",
          }}
        >
          {dataDetail.has_save_warkat
            ? "PERBAHARUI KE DATA WARKAT"
            : "SIMPAN KE DATA WARKAT"}
        </Button>

        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={<PrintIcon />}
          sx={{
            fontWeight: "bold",
            flexGrow: 1,
            backgroundColor: (t) => t.palette.info.main,
            color: "white",
            "&:hover": { backgroundColor: (t) => t.palette.info.dark },
          }}
        >
          CETAK
        </Button>

        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontWeight: "bold",
            flexGrow: 1,
            backgroundColor: (t) => t.palette.grey[300],
            color: (t) => t.palette.text.primary,
            "&:hover": { backgroundColor: (t) => t.palette.grey[400] },
          }}
        >
          TUTUP
        </Button>
      </DialogActions>
      <PinModal
        open={openPinDialog}
        onClose={() => setOpenPinDialog(false)}
        title="Verifikasi PIN"
        subtitle="Masukkan 6 digit PIN untuk melihat Rincian Akumulasi Omzet Server dan Selisih."
        correctPin={currentPin}
        onSuccess={() => {
          setHasVerifyPIN(true);
          setOpenPinDialog(false);
        }}
      />
    </Dialog>
  );
};

export default DetailSetoranHarianModal;
